import React, { useState, useContext } from "react";
import PlatformCtx from "../state/PlatformCtx";
import CustomSelect from "../shared/CustomSelect";
import { ALL_MODULE_KEYS, MODULE_GROUP_ORDER, modulesInGroup, buildLabelMap, AI_SUPPORTED_MODULES, moduleLabel } from "../modules/registry";
import SearchableSelect from "../shared/SearchableSelect";
import authFetch from "../authFetch";
import { COUNTRIES, CITIES_BY_COUNTRY, PROPERTY_TYPES } from "../utils/geo";
import T from "../i18n/translations";
import MissingTranslationsCard from "../shared/MissingTranslationsCard";

export default function BrandingTab({state,dispatch,pdispatch,wsInfo}){
  const lang = state.lang || "en";
  const t = T[lang];
  const isRTL = lang === "ar";
  const [section, setSection] = useState("identity"); // identity | modules

  /* ── Draft state — changes go here until confirmed ── */
  const [draftName,    setDraftName]    = useState(wsInfo?.name || state.appName  || "Command");
  const [draftTagline, setDraftTagline] = useState(state.tagline  || "");
  const [draftCurrency,setDraftCurrency]= useState(state.currency || "SAR");
  const [confirmed, setConfirmed] = useState(false);
  // Workspace identity
  const [subdomainDraft,setSubdomainDraft]=useState((wsInfo?.subdomain)||"");
  const [subdomainSaved,setSubdomainSaved]=useState(false);
  const [provisionState,setProvisionState]=useState(null);
  const [wsDeleteStep,setWsDeleteStep]=useState(0);
  const [wsResetStep,setWsResetStep]=useState(0);
  const [wsResetting,setWsResetting]=useState(false);
  const platformCtx=useContext(PlatformCtx);
  // Nav ordering state — keys derived from module registry
  const [draftNavOrder, setDraftNavOrder] = useState(state.navOrder || ALL_MODULE_KEYS);
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  // Module subtitles
  const [subDrafts,setSubDrafts]=useState({...(state.moduleSubtitles||{})});
  // Household profile
  const hd=state.householdData||{};
  const [hdCity,setHdCity]=useState(hd.city||"");
  const [hdCountry,setHdCountry]=useState(hd.country||"Saudi Arabia");
  const [hdPropertyType,setHdPropertyType]=useState(hd.propertyType||"villa");
  const [hdResidents,setHdResidents]=useState(hd.residents||1);
  const [hdStaffCount,setHdStaffCount]=useState(hd.staffCount||0);
  const [hdSaved,setHdSaved]=useState(false);

  const saveAll = () => {
    const wsName=draftName.trim()||"TR OS";
    dispatch({type:"SET_NAV_ORDER", order: draftNavOrder});
    dispatch({type:"SET_CURRENCY",  currency: draftCurrency});
    dispatch({type:"SET_APP_NAME",  name:    wsName});
    dispatch({type:"SET_TAGLINE",   tagline: draftTagline.trim()});
    /* Workspace name and app name are unified — also sync to the platform workspace registry */
    if(pdispatch&&wsInfo&&wsName&&wsName!==wsInfo.name){
      pdispatch({type:"UPDATE_WORKSPACE",id:wsInfo.id,patch:{name:wsName}});
    }
    setConfirmed(true);
    setTimeout(()=>setConfirmed(false), 3000);
  };


  return (
    <div>
      {/* Section nav — full-width with centred labels */}
      <div className="branding-sec-nav" style={{display:"flex",gap:8,marginBottom:20,width:"100%"}}>
        {[{k:"identity",l:t.appIdentity},{k:"modules",l:t.modulesAndNav}].map(s=>(
          <button key={s.k} className={`btn btn-sm ${section===s.k?"btn-p":"btn-s"}`} style={{flex:1,borderRadius:12,padding:"12px 10px",fontSize:13,fontWeight:600,textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setSection(s.k)}>{s.l}</button>
        ))}
      </div>

      {/* ── APP IDENTITY ── */}
      {section==="identity"&&(
        <div style={{display:"flex",flexDirection:"column",gap:18}}>
          {/* Workspace Name & Tagline */}
          <div className="card">
            <div className="ch"><div className="ct">{t.workspaceName}</div></div>
            <div style={{padding:18}}>
              <label className="fl">{t.workspaceName}</label>
              <input className="fi" value={draftName} onChange={e=>setDraftName(e.target.value)}
                placeholder={t.workspaceNamePh} maxLength={32}
                style={{marginBottom:4,fontSize:15,fontWeight:500}}/>
              <div style={{fontSize:11,color:"var(--g400)",fontFamily:"var(--fm)",marginBottom:14}}>{draftName.trim().length}/32</div>
              <label className="fl">{t.subtitleTagline} <span style={{color:"var(--g400)",fontWeight:400}}>({t.optional})</span></label>
              <input className="fi" value={draftTagline} onChange={e=>setDraftTagline(e.target.value)}
                placeholder={t.taglinePh} maxLength={60}
                style={{marginBottom:4}}/>
              <div style={{fontSize:11,color:"var(--g400)",fontFamily:"var(--fm)"}}>{draftTagline.trim().length}/60 · Shown below the app name on login screen and sidebar</div>
              <div style={{marginTop:16,borderTop:"1px solid var(--g100)",paddingTop:14}}>
                <label className="fl">{t.workspaceCurrency}</label>
                <select className="fs" value={draftCurrency} onChange={e=>setDraftCurrency(e.target.value)} style={{maxWidth:180}}>
                  {["SAR","USD","EUR","GBP","AED","KWD","BHD","OMR","QAR","EGP","JOD","LBP","MAD","TRY","INR","PKR","BDT","CNY","JPY","KRW","SGD","MYR","IDR","THB","PHP","AUD","CAD","CHF","SEK","NOK","DKK","PLN","CZK","HUF","RUB","BRL","MXN","ARS","CLP","COP","PEN","ZAR","NGN","KES","GHS","TZS"].map(c=><option key={c} value={c}>{c}</option>)}
                </select>
                <div style={{fontSize:11,color:"var(--g400)",fontFamily:"var(--fm)",marginTop:4,marginBottom:14}}>{t.currencyDesc}</div>
              </div>
              <button className="btn btn-p btn-sm" onClick={saveAll}>{confirmed?(isRTL?"تم الحفظ ✓":"Saved ✓"):t.save}</button>
            </div>
          </div>


          {/* Household Profile */}
          {(()=>{
            const countryOpts=COUNTRIES.map(c=>({value:c,label:c}));
            const cityOpts=(CITIES_BY_COUNTRY[hdCountry]||[]).map(c=>({value:c,label:c}));
            const residentsOpts=Array.from({length:20},(_,i)=>({value:String(i+1),label:String(i+1)}));
            const staffCountOpts=Array.from({length:21},(_,i)=>({value:String(i),label:String(i)}));
            return(
          <div className="card" style={{padding:20}}>
            <div style={{fontSize:11,fontWeight:600,color:"var(--g500)",textTransform:"uppercase",letterSpacing:".07em",marginBottom:16}}>{t.householdProfile}</div>
            <div className="fr" style={{marginBottom:12}}>
              <div className="fg"><label className="fl">{t.country}</label>
                <SearchableSelect options={countryOpts} value={hdCountry} onChange={v=>{setHdCountry(v);setHdCity(CITIES_BY_COUNTRY[v]?.[0]||"");}} placeholder={t.selectCountry}/>
              </div>
              <div className="fg"><label className="fl">{t.city}</label>
                <SearchableSelect options={cityOpts} value={hdCity} onChange={v=>setHdCity(v)} placeholder={t.selectCity}/>
              </div>
            </div>
            <div className="fg" style={{marginBottom:12}}>
              <label className="fl">{t.propertyType}</label>
              <SearchableSelect options={PROPERTY_TYPES} value={hdPropertyType} onChange={v=>setHdPropertyType(v)} placeholder={t.selectPropertyType}/>
            </div>
            <div className="fr" style={{marginBottom:14}}>
              <div className="fg"><label className="fl">{t.residents}</label>
                <SearchableSelect options={residentsOpts} value={String(hdResidents)} onChange={v=>setHdResidents(parseInt(v)||1)} placeholder={t.selectResidents}/>
              </div>
              <div className="fg"><label className="fl">{t.staffCount}</label>
                <SearchableSelect options={staffCountOpts} value={String(hdStaffCount)} onChange={v=>setHdStaffCount(parseInt(v)||0)} placeholder={t.selectStaffCount}/>
              </div>
            </div>
            <button className="btn btn-p btn-sm" onClick={()=>{
              dispatch({type:"SET_HOUSEHOLD_DATA",patch:{city:hdCity,country:hdCountry,propertyType:hdPropertyType,residents:hdResidents,staffCount:hdStaffCount}});
              setHdSaved(true);setTimeout(()=>setHdSaved(false),2500);
            }}>{hdSaved?(isRTL?"تم الحفظ ✓":"Saved ✓"):t.save}</button>
          </div>);
          })()}

          {/* Translation audit — surfaces missing nameAr and offers
              one-click bulk AI translate via Claude Haiku. */}
          <MissingTranslationsCard state={state} dispatch={dispatch} lang={lang}/>

          {/* ── Restart onboarding ────────────────────────────────
              Resets the onboardingState config key so the wizard
              re-fires on next owner session. Non-destructive —
              doesn't touch workspace data, only the flag that
              gates the first-run overlay. */}
          <div className="card" style={{padding:20,marginTop:16}}>
            <div style={{fontSize:13,fontWeight:700,color:"var(--ink)",marginBottom:6,letterSpacing:"-.005em"}}>
              {isRTL?"إعادة تشغيل الجولة التعريفية":"Restart onboarding"}
            </div>
            <div style={{fontSize:12.5,color:"var(--g500)",lineHeight:1.55,marginBottom:12}}>
              {isRTL
                ?"أعد تشغيل جولة الوحدات للمالك. لن يتم مسح أي بيانات — فقط يتم إعادة تعيين العلامة."
                :"Replay the module walkthrough for the workspace owner. No data is deleted — only the flag is reset."}
            </div>
            <button
              className="btn btn-sm"
              style={{borderColor:"var(--g300)",color:"var(--ink)",background:"none",fontWeight:600}}
              onClick={()=>{
                /* manuallyRestarted bypasses the auto-complete-for-existing-
                   users hook in WorkspaceApp — otherwise that hook would
                   immediately flip complete back to true since the workspace
                   has data (which is expected for any re-run). */
                dispatch({type:"SET_CONFIG_KEY",payload:{key:"onboardingState",value:{
                  complete:false,
                  currentStep:0,
                  skipped:[],
                  startedAt:null,
                  updatedAt:new Date().toISOString(),
                  completedAt:null,
                  manuallyRestarted:true,
                  autoCompletedForExistingUser:false,
                }}});
              }}
            >
              {isRTL?"إعادة تشغيل الآن":"Restart now"}
            </button>
          </div>

          {/* Workspace Identity */}
          {pdispatch&&wsInfo&&(
            /* Danger zone — reset dashboard, delete workspace */
            <div className="card" style={{padding:20,border:"1.5px solid var(--red-bd)",background:"var(--red-bg)"}}>
              <div style={{fontSize:14,fontWeight:700,color:"var(--red)",textTransform:"uppercase",letterSpacing:".09em",marginBottom:16,paddingBottom:10,borderBottom:"1px solid var(--red-bd)"}}>{t.dangerZone}</div>

              {/* ── Reset dashboard ── */}
              <div style={{fontSize:11,fontWeight:600,color:"var(--red)",textTransform:"uppercase",letterSpacing:".07em",marginBottom:10}}>{t.resetDashboard}</div>
              {wsResetStep===0&&(
                <>
                  <div style={{fontSize:13,color:"var(--g600)",lineHeight:1.6,marginBottom:8}}>{t.resetWarningShort}</div>
                  <button className="btn btn-sm" style={{borderColor:"var(--red-bd)",color:"var(--red)",background:"none"}} onClick={()=>setWsResetStep(1)}>{t.resetDashboard}…</button>
                </>
              )}
              {wsResetStep===1&&(
                <>
                  <div style={{fontSize:13,fontWeight:700,color:"var(--red)",marginBottom:6}}>{t.resetWarningLong}</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <button className="btn btn-sm" style={{borderColor:"var(--red-bd)",color:"var(--red)",background:"none",fontWeight:700}} onClick={()=>setWsResetStep(2)}>{isRTL?"أفهم — متابعة":"I understand — continue"}</button>
                    <button className="btn btn-sm" onClick={()=>setWsResetStep(0)}>{t.cancel}</button>
                  </div>
                </>
              )}
              {wsResetStep===2&&(
                <>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    <input id="ws-reset-confirm-input2" className="fi" placeholder={t.typeReset} style={{maxWidth:160,fontFamily:"'JetBrains Mono',monospace",textTransform:"uppercase"}}/>
                    <button className="btn btn-sm" disabled={wsResetting} style={{background:"var(--red)",color:"#fff",border:"none",fontWeight:700,opacity:wsResetting?0.6:1}} onClick={async()=>{
                      const val=(document.getElementById("ws-reset-confirm-input2")?.value||"").trim().toUpperCase();
                      if(val!=="RESET"){document.getElementById("ws-reset-confirm-input2").style.border="2px solid var(--red)";return;}
                      setWsResetting(true);
                      try{
                        const r=await authFetch("/api/auth",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({action:"reset_dashboard",ws_key:state.wsKey})});
                        const d=await r.json();
                        if(d.ok){setWsResetStep(0);setWsResetting(false);window.location.reload();}
                        else{setWsResetting(false);alert(d.error||"Reset failed.");}
                      }catch{setWsResetting(false);alert("Network error — please try again.");}
                    }}>{wsResetting?"Resetting…":"Reset all data"}</button>
                    <button className="btn btn-sm" onClick={()=>setWsResetStep(0)}>Cancel</button>
                  </div>
                </>
              )}

              <div style={{borderTop:"1px solid var(--red-bd)",margin:"18px 0 14px"}}/>

              {/* ── Delete workspace ── */}
              <div style={{fontSize:11,fontWeight:600,color:"var(--red)",textTransform:"uppercase",letterSpacing:".07em",marginBottom:10}}>Delete workspace</div>
              {wsDeleteStep===0&&(
                <>
                  <div style={{fontSize:13,color:"var(--g600)",lineHeight:1.6,marginBottom:12}}>Permanently delete this workspace and all its data. This cannot be undone.</div>
                  <button className="btn btn-sm" style={{borderColor:"var(--red-bd)",color:"var(--red)",background:"none"}} onClick={()=>setWsDeleteStep(1)}>Delete this workspace…</button>
                </>
              )}
              {wsDeleteStep===1&&(
                <>
                  <div style={{fontSize:13,fontWeight:700,color:"var(--red)",marginBottom:6}}>All data will be permanently deleted immediately.</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <button className="btn btn-sm" style={{borderColor:"var(--red-bd)",color:"var(--red)",background:"none",fontWeight:700}} onClick={()=>setWsDeleteStep(2)}>I understand — continue</button>
                    <button className="btn btn-sm" onClick={()=>setWsDeleteStep(0)}>Cancel</button>
                  </div>
                </>
              )}
              {wsDeleteStep===2&&(
                <>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    <input id="ws-delete-confirm-input2" className="fi" placeholder="Type DELETE" style={{maxWidth:160,fontFamily:"'JetBrains Mono',monospace",textTransform:"uppercase"}}/>
                    <button className="btn btn-sm" style={{background:"var(--red)",color:"#fff",border:"none",fontWeight:700}} onClick={async()=>{
                      const val=(document.getElementById("ws-delete-confirm-input2")?.value||"").trim().toUpperCase();
                      if(val!=="DELETE"){document.getElementById("ws-delete-confirm-input2").style.border="2px solid var(--red)";return;}
                      /* CRITICAL: await the server delete before reloading. Previously this
                         was fire-and-forget followed by window.location.reload(), which
                         caused the browser to cancel the in-flight request on navigation.
                         The server never received the delete, the workspace row stayed
                         status='active', and the email could not be re-registered. */
                      try{
                        const r=await authFetch("/api/delete-workspace",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({ws_key:wsInfo.wsKey,ws_id:wsInfo.id}),keepalive:true});
                        if(!r.ok){
                          const d=await r.json().catch(()=>({}));
                          alert("Failed to delete workspace: "+(d.error||r.statusText||"unknown error"));
                          return;
                        }
                      }catch(e){
                        alert("Network error while deleting workspace. Please try again.");
                        return;
                      }
                      try{if(wsInfo.wsKey)localStorage.removeItem(wsInfo.wsKey);}catch{}
                      pdispatch({type:"REMOVE_WORKSPACE",id:wsInfo.id});
                      window.location.reload();
                    }}>Delete workspace forever</button>
                    <button className="btn btn-sm" onClick={()=>setWsDeleteStep(0)}>Cancel</button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── MODULES & NAVIGATION ── */}
      {section==="modules"&&(()=>{
        const agents=platformCtx?.aiAgents||{};
        const wsOff=state.wsAiDisabled||{};
        const allOff=state.wsAiAllDisabled||false;
        /* Modules that support AI per-module toggle — derived from registry */
        const AI_KEY_MAP=Object.fromEntries([...AI_SUPPORTED_MODULES].map(k=>[k,true]));
        /* ── Package-based module permissions ── */
        const wsPkg=(platformCtx?.platformPackages||[]).find(p=>p.id===(wsInfo?.packageId||wsInfo?.plan));
        const pkgModules=wsPkg?new Set(wsPkg.modules||[]):null;/* null = no package, all allowed */
        const isAllowed=(key)=>key==="Dashboard"||!pkgModules||pkgModules.has(key);
        const pkgName=wsPkg?.name||"";
        /* Find next tier that includes a given module */
        const allPkgs=(platformCtx?.platformPackages||[]);
        const getUpgradePkg=(key)=>allPkgs.find(p=>p.id!==wsPkg?.id&&(p.modules||[]).includes(key)&&allPkgs.indexOf(p)>allPkgs.indexOf(wsPkg));
        const pmn=platformCtx?.platformModuleNames||{};
        /* Module labels and group structure — derived from registry.
           Labels must follow current UI language; group headers use the
           existing Arabic strings from translations when available. */
        const MOD_LABELS=buildLabelMap(isRTL);
        const modLbl=(key)=>moduleLabel(key,{pmn,isRTL});
        const GROUP_LABELS_EN={COMMAND:"Command",FINANCE:"Finance",INVESTMENTS:"Investments",OPERATIONS:"Operations",PRODUCTIVITY:"Productivity"};
        const GROUP_LABELS_AR={COMMAND:"القيادة",FINANCE:"المالية",INVESTMENTS:"الاستثمارات",OPERATIONS:"العمليات",PRODUCTIVITY:"الإنتاجية"};
        const GROUP_LABELS=isRTL?GROUP_LABELS_AR:GROUP_LABELS_EN;
        const NAV_GROUPS=MODULE_GROUP_ORDER.map(g=>({key:g,label:GROUP_LABELS[g],members:modulesInGroup(g)}));
        const moveWithinGroup=(key,direction)=>{
          const group=NAV_GROUPS.find(g=>g.members.includes(key));
          if(!group)return;
          const arr=[...draftNavOrder];
          const globalIdx=arr.indexOf(key);
          const groupMembersInOrder=arr.filter(k=>group.members.includes(k));
          const posInGroup=groupMembersInOrder.indexOf(key);
          if(direction===-1&&posInGroup===0)return;
          if(direction===1&&posInGroup===groupMembersInOrder.length-1)return;
          const neighbour=groupMembersInOrder[posInGroup+direction];
          const neighbourGlobalIdx=arr.indexOf(neighbour);
          [arr[globalIdx],arr[neighbourGlobalIdx]]=[arr[neighbourGlobalIdx],arr[globalIdx]];
          setDraftNavOrder(arr);
        };
        const canDrop=(dragKey,dropKey)=>{
          const dragGroup=NAV_GROUPS.find(g=>g.members.includes(dragKey));
          const dropGroup=NAV_GROUPS.find(g=>g.members.includes(dropKey));
          return dragGroup&&dropGroup&&dragGroup.key===dropGroup.key;
        };

        return(
          <div className="ga2" style={{gap:18}}>
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {/* Master AI toggle */}
              <div className="card" style={{overflow:"hidden"}}>
                <div style={{display:"flex",alignItems:"center",gap:14,padding:"12px 18px",background:"var(--gold-bg)",borderBottom:"1px solid var(--gold-bd)"}}>
                  <div className="ai-dot" style={{flexShrink:0}}/>
                  <div style={{flex:1}}>
                    <div style={{fontSize:14,fontWeight:600,letterSpacing:"-.01em"}}>AI for All Modules</div>
                    <div style={{fontSize:11,color:"var(--g400)",marginTop:1}}>Master switch — disable AI across every module at once</div>
                  </div>
                  <div className={`toggle ${!allOff?"on":""}`} onClick={()=>dispatch({type:"SET_WS_AI_ALL_DISABLED",disabled:!allOff})}/>
                </div>
              </div>

              {NAV_GROUPS.map(group=>{
                /* Take ordering from draftNavOrder, but always include every
                   current registry member of the group. This keeps user
                   reordering intact while auto-adding newly-registered modules
                   (e.g. Smart Home) that existed after navOrder was saved. */
                const ordered=draftNavOrder.filter(k=>group.members.includes(k));
                const missing=group.members.filter(k=>!ordered.includes(k));
                const groupKeys=[...ordered,...missing];
                return(
                  <div key={group.key} className="card">
                    <div className="ch">
                      <div>
                        <div className="ct">{group.label}</div>
                        <div style={{fontSize:11,color:"var(--g400)",marginTop:2}}>Toggle visibility · reorder · set subtitles</div>
                      </div>
                    </div>
                    <div style={{padding:"4px 0"}}>
                      {groupKeys.map((key,posInGroup)=>{
                        const allowed=isAllowed(key);
                        const isOn=allowed&&((state.modules||{})[key]!==false||key==="Dashboard");
                        const hasAi=allowed&&AI_KEY_MAP[key]&&(agents[key]||{}).enabled!==false;
                        const aiOff=wsOff[key]||false;
                        const isDraggingThis=dragging===key;
                        const isDragTarget=dragOver===key&&canDrop(dragging||"",key);
                        const upgradePkg=!allowed?getUpgradePkg(key):null;
                        return(
                          <div key={key} style={{borderBottom:posInGroup<groupKeys.length-1?"1px solid var(--g100)":"none"}}>
                            <div
                              draggable={allowed}
                              onDragStart={()=>allowed&&setDragging(key)}
                              onDragOver={e=>{e.preventDefault();if(canDrop(dragging||"",key))setDragOver(key);}}
                              onDrop={()=>{
                                if(!dragging||dragging===key||!canDrop(dragging,key))return;
                                const arr=[...draftNavOrder];
                                const fromIdx=arr.indexOf(dragging);
                                const toIdx=arr.indexOf(key);
                                arr.splice(fromIdx,1);
                                arr.splice(toIdx,0,dragging);
                                setDraftNavOrder(arr);
                                setDragging(null);setDragOver(null);
                              }}
                              onDragEnd={()=>{setDragging(null);setDragOver(null);}}
                              style={{
                                display:"flex",alignItems:"center",gap:10,padding:"10px 18px",
                                background:isDragTarget?"var(--g50)":isDraggingThis?"var(--g100)":"transparent",
                                cursor:allowed?"grab":"default",opacity:!allowed?0.4:!isOn?0.45:1,
                                transition:"background .08s",
                                borderLeft:isDragTarget?"3px solid var(--primary,var(--ink))":"3px solid transparent",
                              }}>
                              {allowed?<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="var(--g300)" strokeWidth="1.4">
                                <line x1="1" y1="3" x2="11" y2="3"/><line x1="1" y1="6" x2="11" y2="6"/><line x1="1" y1="9" x2="11" y2="9"/>
                              </svg>:<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--g300)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>}
                              <div style={{flex:1,fontSize:14,fontWeight:500,letterSpacing:"-.01em"}}>{modLbl(key)}</div>
                              {!allowed&&<span style={{fontSize:10,fontWeight:600,color:"var(--g400)",padding:"3px 10px",borderRadius:20,border:"1px solid var(--g200)",background:"var(--g50)",whiteSpace:"nowrap"}}>{upgradePkg?upgradePkg.name:"Upgrade"}</span>}
                              {allowed&&hasAi&&!allOff&&(
                                <div style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",padding:"3px 10px",borderRadius:20,border:"1px solid "+(aiOff?"var(--g200)":"var(--gold-bd)"),background:aiOff?"transparent":"var(--gold-bg)",fontSize:11,fontWeight:600,color:aiOff?"var(--g400)":"var(--amb)",transition:"all .15s"}} onClick={()=>dispatch({type:"SET_WS_AI_DISABLED",moduleKey:key,disabled:!aiOff})} title={aiOff?"Enable AI":"Disable AI"}>
                                  <span style={{width:5,height:5,borderRadius:"50%",background:aiOff?"var(--g300)":"var(--gold)",flexShrink:0}}/>
                                  AI
                                </div>
                              )}
                              {allowed?<div className={`toggle ${isOn?"on":""}`} onClick={()=>dispatch({type:"SET_MODULE",key:key,on:!isOn})}/>:<div className="toggle" style={{opacity:0.3,pointerEvents:"none"}}/>}
                              {allowed&&<div style={{display:"flex",gap:2}}>
                                <button className="btn btn-g btn-xs"
                                  disabled={posInGroup===0}
                                  style={{opacity:posInGroup===0?0.3:1,padding:"2px 7px",minWidth:0,fontSize:12}}
                                  onClick={()=>moveWithinGroup(key,-1)}>↑</button>
                                <button className="btn btn-g btn-xs"
                                  disabled={posInGroup===groupKeys.length-1}
                                  style={{opacity:posInGroup===groupKeys.length-1?0.3:1,padding:"2px 7px",minWidth:0,fontSize:12}}
                                  onClick={()=>moveWithinGroup(key,1)}>↓</button>
                              </div>}
                            </div>
                            {allowed&&<div style={{padding:"4px 18px 10px",display:"flex",gap:8,alignItems:"center"}}>
                              <input
                                className="fi"
                                value={subDrafts[key]||""}
                                onChange={e=>{const v=e.target.value;setSubDrafts(p=>({...p,[key]:v}));dispatch({type:"SET_MODULE_SUB",key:key,sub:v});}}
                                placeholder={`Subtitle for ${MOD_LABELS[key]||key}…`}
                                style={{fontSize:12,flex:1,color:"var(--g500)"}}
                              />
                            </div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {/* Order actions — full-width, centred */}
              <div style={{display:"flex",gap:8,width:"100%"}}>
                <button className="btn btn-p btn-sm" style={{flex:1,textAlign:"center",justifyContent:"center"}} onClick={()=>{dispatch({type:"SET_NAV_ORDER",order:draftNavOrder});setConfirmed(true);setTimeout(()=>setConfirmed(false),2000);}}>
                  {confirmed?"Saved ✓":"Save Order"}
                </button>
                <button className="btn btn-g btn-sm" style={{flex:1,textAlign:"center",justifyContent:"center"}} onClick={()=>setDraftNavOrder(ALL_MODULE_KEYS)}>Reset to Default</button>
              </div>
              <div style={{fontSize:13,color:"var(--g500)",lineHeight:1.7,background:"var(--blu-bg)",border:"1px solid var(--blu-bd)",borderRadius:"var(--r)",padding:14}}>
                <strong style={{color:"var(--blu)"}}>AI Privacy Notice</strong><br/>
                AI has no access to who owns this workspace or any personal references. All data is analysed on an anonymous and randomised basis. No workspace data is stored by the AI provider or used for training.
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
